import {
  FlightsCabinClass,
  FlightsFaresSeparationType,
  FlightsFlexibleDiscoverySearch,
  FlightsListingsViewType,
  FlightsQueryState,
  FlightsSearchMetadata,
  FlightsShoppingPwaFlightSearchFormLoaded,
  FlightsShoppingPwaFlightSearchResultsFlightsSearch,
  FlightsShoppingPwaFlightSearchResultsLoadingFlightsSearch
} from '__generated__/api';
import { ClientLogger } from 'bernie-client';
import { getBexApiContext } from 'bernie-context';
import { KeyedObject } from 'bernie-core/dist/source';
import { codeSplit, Controller, FetchOptions, FetchPageOptions, StoreContainer } from 'bernie-core';
import { AnalyticsStore, ContextStore, ExperimentStore, PageStore } from 'bernie-plugin-mobx';
import { Localization } from 'bernie-l10n';
import { PageData } from 'bernie-http';
import { NOOP_LOGGER, SystemEvent, SystemEventLevel } from 'bernie-logger';
import { action, flow } from 'mobx';
import { FlightsMetaFIStoFSR } from 'experiments/Flights_meta_ow_fis_to_fsr_46906/Flights_meta_ow_fis_to_fsr_46906';
import { isDomesticJourney, setIsDomestic } from 'utility/flights-controller-utils';

import {
  AdvertisingStore,
  BannersStore,
  CheapestListingInfoStore,
  ClientMetadataStore,
  CreditRedemptionStore,
  DetailsAndFaresStore,
  DialogsStore,
  ErrorStore,
  FlexibleSearchResultsStore,
  FlexibleSearchStore,
  FlightsSearchMetadataStore,
  GaiaIdStore,
  ListingsHeaderPlacardsStore,
  ListingsStore,
  OpinionLabStore,
  PageTitleStore,
  RewardsPricingInfoStore,
  SearchCriteriaStore,
  SearchFormResponseStore,
  SearchFormStore,
  SearchResultsStore,
  SortFilterStore,
  StepIndicatorStore,
  VirtualAgentStore
} from 'stores';
import { SearchQueryAdapter } from 'stores/adapters';
import {
  FlightsSearchPageIds,
  generateRequestOptions,
  getPageName,
  hasSortOrFilters,
  isFlightsPredictiveSearch,
  needSearchFsr,
  PageId,
  trackAnalytics
} from 'utility';
import analyticsWrapper from 'utility/analytics-wrapper';
import { NavigationId } from 'utility/navigation-identity';
import { airSmartShopping } from 'experiments/Air_Smart_Shopping_44809';
import PaginationConfig = FlightsShoppingPwaFlightSearchResultsLoadingFlightsSearch.PaginationConfig;
import FlightsLoadedListingResult = FlightsShoppingPwaFlightSearchResultsFlightsSearch.FlightsLoadedListingResultInlineFragment;

export class FlightsSearchController implements Controller {
  public pageId: string = PageId.FLIGHTS_SEARCH_ONE_WAY;
  public path = '/Flights-Search*';
  public routeName = 'flights-search';
  public bundles = [];
  public exact = true;
  public component = codeSplit(() => import(/* webpackChunkName: "search" */ '../views/flights-search'));
  public pendingSearchResultsPromises = [];
  public pendingFlexibleSearchResultsPromises = [];
  public pendingSearchFormPromises = [];
  public pendingSearchMetadataPromises = [];
  public clientErrorRenderDelay = 500;
  private logger = ClientLogger.getLoggerWithIdentifier('controller.flightsSearch');
  public prevPageId;
  public journeyCriteria = [];
  public isDomestic = false;

  private getPageIdFromQuery(query): string {
    let pageId = this.pageId;
    if (query) {
      const tripType = SearchQueryAdapter.mapTripType(query.trip);
      const packageType = (query.packageType && `${query.packageType.toUpperCase()}_`) || '';
      pageId = FlightsSearchPageIds[`${packageType}${tripType}_${query.pageId || 0}`] || pageId;
    }

    return pageId;
  }

  private getNavigationIdFromQuery = query =>
    query && query.packageType && query.misId ? NavigationId.PACKAGE : NavigationId.FLIGHT;

  private isOneWay(query): boolean {
    return query?.trip === 'oneway';
  }

  private shouldBlockNonStopCall = (
    query: KeyedObject,
    hasNonStopConfig: boolean,
    hasAdvancedFilters?: number,
    hasSortFilterVariable?: boolean,
    cabinClass?: FlightsCabinClass,
    airline?: string
  ): boolean => {
    if (hasNonStopConfig && this.isOneWay(query)) {
      if (
        hasSortOrFilters(query) ||
        hasAdvancedFilters ||
        hasSortFilterVariable ||
        (cabinClass && cabinClass !== FlightsCabinClass.COACH) ||
        (airline && airline !== '*')
      ) {
        return true;
      }

      return false;
    }

    return true;
  };

  private isOneTraveler(query): boolean {
    const passengers = query?.passengers.split(',') ?? [];
    const travelerDetails = SearchQueryAdapter.mapTravelers(passengers);

    return travelerDetails?.length === 1 && travelerDetails?.[0]?.count === 1;
  }

  private setJourneyCriteria(options): void {
    let journey;
    this.journeyCriteria = [];
    const query = options.query;
    if (this.isOneWay(query)) {
      journey = Object.keys(query).filter(name => name === 'leg1' && /leg/.test(name));
      const locale = options.request.context.locale;
      const localDateFormat = options.request.context.localDateFormat;
      this.journeyCriteria = journey.map(leg => SearchQueryAdapter.mapLeg(query[leg], locale, localDateFormat));
    }
  }

  private async setDomesticJourney(options): Promise<void> {
    this.setJourneyCriteria(options);
    if (this.journeyCriteria.length > 0) {
      const originLocation = this.journeyCriteria[0].origin;
      const destinationLocation = this.journeyCriteria[0].destination;
      const originDestinations = await isDomesticJourney(options, originLocation, destinationLocation, this.logger);
      this.isDomestic = setIsDomestic(originDestinations);
    } else {
      this.isDomestic = false;
    }
  }

  private async setSmartShoppingCriteria(options: FetchOptions): Promise<void> {
    const { searchCriteriaStore } = this.getFetchVariables(options.stores, options.isServer, options.query);
    const query = options.query;
    const experiment = options.stores.get<ExperimentStore>('experiment');
    const credits = options.stores.get<CreditRedemptionStore>('creditRedemptionStore');
    const isSmartShoppingTnlEnabled = airSmartShopping.shouldRender(experiment);
    const isStandalone = this.getNavigationIdFromQuery(query) === NavigationId.FLIGHT;
    await this.setDomesticJourney(options);
    searchCriteriaStore.hasSmartShoppingCriteria =
      isSmartShoppingTnlEnabled &&
      this.isOneTraveler(query) &&
      this.isDomestic &&
      isStandalone &&
      !credits.airCreditIntent;
  }

  private isNonStopConfig = (faresSeparationType: FlightsFaresSeparationType) =>
    faresSeparationType === FlightsFaresSeparationType.NONSTOP_ONLY_BASE ||
    faresSeparationType === FlightsFaresSeparationType.NONSTOP_ONLY_UPSELL ||
    faresSeparationType === FlightsFaresSeparationType.NONSTOP_BASE_AND_UPSELL;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public async fetch(options: FetchOptions): Promise<any> {
    const {
      searchCriteriaStore,
      pageStore,
      searchResultsStore,
      listingsStore,
      detailsAndFaresStore,
      analyticsStore,
      stepIndicatorStore,
      advertisingStore,
      creditRedemptionStore,
      virtualAgentStore,
      pageTitleStore,
      sortFilterStore,
      errorStore,
      variables
    } = this.getFetchVariables(options.stores, options.isServer, options.query);
    const query = options.query;
    this.prevPageId = pageStore.pageId;
    pageStore.pageId = this.getPageIdFromQuery(query);
    pageStore.pageName = getPageName(pageStore.pageId);
    advertisingStore.trigger(pageStore.pageName);
    virtualAgentStore.isVirtualAgentClient = query?.client === 'virtual-agent';
    stepIndicatorStore.setMultiItemPriceToken(query.mipt || '');
    creditRedemptionStore.setAirCreditIntent(!!query.airCreditIntent);
    searchCriteriaStore.setServicingIntent(query.intent);
    await this.setSmartShoppingCriteria(options);
    FlightsMetaFIStoFSR.setMetaCriteria(options, this.isDomestic);

    if (options.isServer) {
      searchCriteriaStore.setCriteriaFromUrl(options);

      return this.fetchServerSide(options);
    }

    //TODO: Catch errors inside needSearch https://jira.expedia.biz/browse/FODF-858
    const searchNeeded = needSearchFsr(options, searchCriteriaStore);
    if (searchNeeded) {
      const promises = [];

      if (listingsStore.listingResult.paginationConfig) {
        searchCriteriaStore.updateSearchCriteriaWithPagination(
          listingsStore.listingResult.paginationConfig[0]?.pagination
        );
        searchCriteriaStore.updateSearchCriteriaWithFaresSeparation(
          listingsStore.listingResult.paginationConfig[0]?.faresSeparationType
        );
      }

      let searchResultsPromise = this.fetchSearchResultsClientSide(options);
      promises.push(searchResultsPromise);

      const hasNonStopConfigWithFiltersValue = this.shouldBlockNonStopCall(
        options.query,
        listingsStore.hasNonStopConfig,
        variables?.searchPreferences?.advancedFilters?.length,
        !!variables?.sortOption,
        variables?.searchPreferences?.cabinClass,
        variables?.searchPreferences?.airline
      );

      if (listingsStore.hasNonStopConfig) {
        if (hasNonStopConfigWithFiltersValue) {
          listingsStore.setHasNonStopConfigWithFilters(true);
        } else {
          listingsStore.setHasNonStopConfigWithFilters(false);
        }
      }

      if (!searchCriteriaStore.hasSmartShoppingCriteria && listingsStore.listingResult.paginationConfig?.length > 1) {
        let paginationConfig = listingsStore.listingResult.paginationConfig.slice(1);
        if (!hasNonStopConfigWithFiltersValue) {
          this.fetchNonStopListingsSubPages(
            listingsStore.listingResult.paginationConfig
              .filter(config => this.isNonStopConfig(config.faresSeparationType))
              .slice(1),
            searchCriteriaStore,
            listingsStore,
            analyticsStore,
            options,
            searchResultsPromise,
            detailsAndFaresStore,
            hasNonStopConfigWithFiltersValue,
            pageTitleStore,
            sortFilterStore,
            stepIndicatorStore,
            errorStore
          );

          paginationConfig = listingsStore.listingResult.paginationConfig.filter(
            config => !this.isNonStopConfig(config.faresSeparationType)
          );
          searchResultsPromise = undefined;
        }

        this.fetchListingsSubPages(
          paginationConfig,
          searchCriteriaStore,
          listingsStore,
          analyticsStore,
          options,
          detailsAndFaresStore,
          pageTitleStore,
          sortFilterStore,
          stepIndicatorStore,
          errorStore,
          hasNonStopConfigWithFiltersValue,
          searchResultsPromise
        );
      }

      if (searchResultsStore.isSubsequentialSearch) {
        this.fetchOnSearchClick(options);
      }
      if (searchCriteriaStore.fetchSearchForm) {
        promises.push(this.fetchSearchFormClientSide(options));
      }
      if (searchCriteriaStore.fetchFlexResults) {
        promises.push(this.fetchFlexibleSearchResultsClientSide(options));
      }
      if (searchCriteriaStore.hasSmartShoppingCriteria) {
        promises.push(this.fetchFlightSearchMetadata(options));
      }

      return Promise.all(promises);
    }

    const bannersStore = options.stores.get<BannersStore>('bannersStore');
    bannersStore.setIsDialogEvent(!searchNeeded);

    return Promise.resolve();
  }

  private async fetchServerSide(options: FetchOptions) {
    const {
      searchCriteriaStore,
      searchFormStore,
      listingsStore,
      searchResultsStore,
      sortFilterStore,
      pageTitleStore,
      errorStore,
      analyticsStore,
      variables,
      clientMetadataStore,
      stepIndicatorStore,
      gaiaIdStore,
      pageStore,
      flexibleSearchResultsStore,
      bannersStore
    } = this.getFetchVariables(options.stores, options.isServer, options.query);

    const response = await searchResultsStore.fetchLoading(
      variables,
      generateRequestOptions(options.request, options.isServer, pageStore.pageId),
      analyticsStore,
      isFlightsPredictiveSearch(options)
    );

    if (isFlightsPredictiveSearch(options) || !response) {
      return;
    }

    let hasNonStopConfigWithFilters = false;
    const shouldBlockNonStopCallValue = this.shouldBlockNonStopCall(
      options.query,
      true,
      variables?.searchPreferences?.advancedFilters?.length,
      !!variables?.sortOption,
      variables?.searchPreferences?.cabinClass,
      variables?.searchPreferences?.airline
    );

    if (shouldBlockNonStopCallValue) {
      hasNonStopConfigWithFilters = true;
    }
    const {
      searchForm,
      sortAndFilterResult,
      listingResult,
      pageTitle,
      clientMetadata,
      gaiaId,
      stepIndicator,
      flexibleSearchResponse
    } = response;
    searchFormStore.hydrate({ searchForm });
    listingsStore.hydrate({ listingResult });
    pageTitleStore.hydrate({ pageTitle });
    sortFilterStore.hydrate({ sortAndFilterResult });
    clientMetadataStore.hydrate({ clientMetadata });
    gaiaIdStore.hydrate({ gaiaId });
    stepIndicatorStore.hydrate({ stepIndicator });
    searchCriteriaStore.forceFetch = false;
    searchCriteriaStore.newSearch = true;
    flexibleSearchResultsStore.showGridFlag = searchCriteriaStore.shouldShowFlexSearch();
    pageTitleStore.setDisclaimerSubTitleLoadingState();
    listingsStore.setLoading(true);
    listingsStore.setHasNonStopConfig();
    listingsStore.setHasNonStopConfigWithFilters(hasNonStopConfigWithFilters);
    bannersStore.refreshBanner = !bannersStore.refreshBanner;
    flexibleSearchResultsStore.hydrate(
      Object.assign(FlexibleSearchResultsStore.DEFAULTS, {
        flexibleSearchResponse: {
          subTitle: flexibleSearchResponse?.subTitle,
          title: flexibleSearchResponse?.title,
          titleAccessibility: flexibleSearchResponse?.titleAccessibility,
          searchTriggerAction: flexibleSearchResponse?.searchTriggerAction,
          flexGridType: flexibleSearchResponse?.flexGridType,
          loadingStateAccessibility: flexibleSearchResponse?.loadingStateAccessibility
        },
        showGridFlag: searchCriteriaStore.shouldShowFlexSearch()
      })
    );
    errorStore.noApiResponse = false;
    sortFilterStore.showFiltersSkeleton = true;
    if (listingsStore.listingResult.paginationConfig) {
      searchCriteriaStore.updateSearchCriteriaWithPagination(
        listingsStore.listingResult.paginationConfig[0]?.pagination
      );
      searchCriteriaStore.updateSearchCriteriaWithFaresSeparation(
        listingsStore.listingResult.paginationConfig[0]?.faresSeparationType
      );
      Object.assign(variables, searchCriteriaStore.searchCriteria);
    }
    let updatedVariables = { ...variables };

    if (variables.faresSeparationType === FlightsFaresSeparationType.NONSTOP_ONLY_BASE) {
      updatedVariables = Object.assign({}, variables);
      updatedVariables.flightsListingsViewType = FlightsListingsViewType.MIXED;
    }

    searchResultsStore.setFlightsSearchResultLoadedRequest(
      searchResultsStore.createFlightsSearchLoadedRequest(options, updatedVariables, pageStore.pageId)
    );

    try {
      const { crossSortEnabled } = options.query;
      if (crossSortEnabled === 'true') {
        sortFilterStore.crossSortEnabled = true;
      }
    } catch {}
  }

  private resolveFinalSlices(
    listingsStore: ListingsStore,
    errorStore: ErrorStore,
    pageTitleStore: PageTitleStore,
    searchCriteriaStore: SearchCriteriaStore,
    sortFilterStore: SortFilterStore,
    stepIndicatorStore: StepIndicatorStore,
    hasNonStopConfigWithFiltersValue?: boolean
  ) {
    if (
      (listingsStore.hasNonStopSlicesResolved && listingsStore.hasMultiStopSlicesResolved) ||
      (listingsStore.hasMultiStopSlicesResolved && hasNonStopConfigWithFiltersValue)
    ) {
      if (listingsStore?.listingResult?.listings?.length === 0) {
        errorStore.setClientFetchFailure(true);
        listingsStore.setLoading(false);
        pageTitleStore.loading = false;
        this.logger.logEvent(new SystemEvent(SystemEventLevel.INFO, 'FSR_FETCH_EMPTY_RESPONSE'));
      } else {
        listingsStore.setLoading(false);
        pageTitleStore.loading = false;

        searchCriteriaStore.newSearch = false;
        sortFilterStore.showFiltersSkeleton = false;
        sortFilterStore.loading = false;
        this.pendingSearchResultsPromises = [];

        stepIndicatorStore.setStepIndicatorVisibility(true);

        const skipMenu = document.querySelector<HTMLElement>('.uitk-skip-link');
        skipMenu?.parentElement?.focus();
      }
      listingsStore.setHasNonStopListingError(false);
    }
  }

  private async fetchListingsSubPages(
    paginationConfig: PaginationConfig[],
    searchCriteriaStore: SearchCriteriaStore,
    listingsStore: ListingsStore,
    analyticsStore: AnalyticsStore,
    options: FetchOptions,
    detailsAndFaresStore: DetailsAndFaresStore,
    pageTitleStore: PageTitleStore,
    sortFilterStore: SortFilterStore,
    stepIndicatorStore: StepIndicatorStore,
    errorStore: ErrorStore,
    hasNonStopConfigWithFiltersValue: boolean,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    searchResultsPromise?: Promise<any>
  ) {
    listingsStore.multiStopListingResult.listings = [];
    listingsStore.setHasMultiStopSlicesResolved(false);
    const promises = [];
    promises.push(searchResultsPromise);
    paginationConfig.forEach(config => {
      searchCriteriaStore.updateSearchCriteriaWithPagination(config.pagination);
      searchCriteriaStore.updateSearchCriteriaWithFaresSeparation(config.faresSeparationType);
      if (config.faresSeparationType === FlightsFaresSeparationType.ONLY_UPSELL) {
        detailsAndFaresStore.setIsUpsellLoaded(false);
      }

      const { variables, searchResultsStore, analyticsStore, pageStore } = this.getFetchVariables(
        options.stores,
        options.isServer,
        options.query
      );

      const promise = searchResultsStore.fetchResults(
        variables,
        generateRequestOptions(options.request, options.isServer, pageStore.pageId),
        analyticsStore,
        false,
        hasNonStopConfigWithFiltersValue
      );
      promises.push(promise);
    });

    return Promise.all(promises)
      .catch(error => {
        this.logger.logEvent(new SystemEvent(SystemEventLevel.ERROR, 'FETCH_FSR_SUB_PAGES'), error);

        return promises;
      })
      .then(responses => {
        responses.slice(1).forEach((response, index) => {
          if (response && (response.listingResult || response.availableFares)) {
            switch (paginationConfig[index].faresSeparationType) {
              case FlightsFaresSeparationType.BASE_AND_UPSELL:
              case FlightsFaresSeparationType.ONLY_BASE:
                if (
                  paginationConfig[index].faresSeparationType === FlightsFaresSeparationType.ONLY_BASE &&
                  listingsStore.hasNonStopListingError
                ) {
                  this.hydrateStoreFSRQuery(options, response, false);
                }
                const { listingResult } = response;
                const subPageListingsStore = new ListingsStore({ listingResult }, NOOP_LOGGER);
                subPageListingsStore.hydrate({ listingResult });
                if (!hasNonStopConfigWithFiltersValue) {
                  listingsStore.updateStoreMultiStopListings(subPageListingsStore);
                  if (paginationConfig[index].faresSeparationType === FlightsFaresSeparationType.BASE_AND_UPSELL) {
                    listingsStore.setHasMultiStopSlicesResolved(true);
                  }
                } else {
                  listingsStore.updateStoreListings(subPageListingsStore);
                }
                break;
              case FlightsFaresSeparationType.ONLY_UPSELL:
                const { availableFares } = response;
                listingsStore.updateStoreListingsWithUpsells(availableFares, detailsAndFaresStore);
                const { clientMetadataStore } = this.getFetchVariables(options.stores, options.isServer, options.query);
                listingsStore.loggingOfferMismatch(availableFares, clientMetadataStore);
                break;
            }
          } else {
            let analytics;
            switch (paginationConfig[index].faresSeparationType) {
              case FlightsFaresSeparationType.ONLY_BASE:
              case FlightsFaresSeparationType.BASE_AND_UPSELL:
                analytics = listingsStore.listingResult.flightsListingsAnalytics?.moreListingsErrorAnalytics;
                break;
            }
            trackAnalytics(analytics, analyticsStore);
          }
        });
      })
      .catch(error => {
        this.logger.logEvent(new SystemEvent(SystemEventLevel.ERROR, 'FSR_SUB_PAGES_HYDRATION'), error);
      })
      .finally(() => {
        detailsAndFaresStore.setIsUpsellLoaded(true);
        listingsStore.setHasMultiStopSlicesResolved(true);
        if (listingsStore.hasNonStopListingError) {
          this.resolveFinalSlices(
            listingsStore,
            errorStore,
            pageTitleStore,
            searchCriteriaStore,
            sortFilterStore,
            stepIndicatorStore,
            hasNonStopConfigWithFiltersValue
          );
        }
      });
  }

  private async fetchNonStopListingsSubPages(
    paginationConfig: PaginationConfig[],
    searchCriteriaStore: SearchCriteriaStore,
    listingsStore: ListingsStore,
    analyticsStore: AnalyticsStore,
    options: FetchOptions,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    searchResultsPromise: Promise<any>,
    detailsAndFaresStore: DetailsAndFaresStore,
    hasNonStopConfigWithFiltersValue: boolean,
    pageTitleStore: PageTitleStore,
    sortFilterStore: SortFilterStore,
    stepIndicatorStore: StepIndicatorStore,
    errorStore: ErrorStore
  ) {
    listingsStore.setHasNonStopSlicesResolved(false);
    const promises = [];
    promises.push(searchResultsPromise);
    paginationConfig.forEach(config => {
      searchCriteriaStore.updateSearchCriteriaWithPagination(config.pagination);
      searchCriteriaStore.updateSearchCriteriaWithFaresSeparation(config.faresSeparationType);
      if (config.faresSeparationType === FlightsFaresSeparationType.NONSTOP_ONLY_UPSELL) {
        detailsAndFaresStore.setIsUpsellLoaded(false);
      }

      const { variables, searchResultsStore, analyticsStore, pageStore } = this.getFetchVariables(
        options.stores,
        options.isServer,
        options.query
      );

      const promise = searchResultsStore.fetchResults(
        variables,
        generateRequestOptions(options.request, options.isServer, pageStore.pageId),
        analyticsStore,
        false,
        hasNonStopConfigWithFiltersValue
      );
      promises.push(promise);
    });

    return Promise.all(promises)
      .catch(error => {
        this.logger.logEvent(new SystemEvent(SystemEventLevel.ERROR, 'FETCH_NON_STOP_FSR_SUB_PAGES'), error);

        return promises;
      })
      .then(responses => {
        responses.slice(1).forEach((response, index) => {
          if (response && (response.listingResult || response.availableFares)) {
            switch (paginationConfig[index].faresSeparationType) {
              case FlightsFaresSeparationType.NONSTOP_ONLY_BASE:
              case FlightsFaresSeparationType.NONSTOP_BASE_AND_UPSELL:
                const { listingResult } = response;
                const subPageListingsStore = new ListingsStore({ listingResult }, NOOP_LOGGER);
                subPageListingsStore.hydrate({ listingResult });
                listingsStore.updateStoreNonStopListings(subPageListingsStore);
                if (
                  paginationConfig[index].faresSeparationType === FlightsFaresSeparationType.NONSTOP_BASE_AND_UPSELL
                ) {
                  listingsStore.setHasNonStopSlicesResolved(true);
                }
                break;
              case FlightsFaresSeparationType.NONSTOP_ONLY_UPSELL:
                const { availableFares } = response;
                listingsStore.updateStoreListingsWithUpsells(availableFares, detailsAndFaresStore);
                const { clientMetadataStore } = this.getFetchVariables(options.stores, options.isServer, options.query);
                listingsStore.loggingOfferMismatch(availableFares, clientMetadataStore);
                break;
            }
          } else {
            let analytics;
            switch (paginationConfig[index].faresSeparationType) {
              case FlightsFaresSeparationType.NONSTOP_ONLY_BASE:
              case FlightsFaresSeparationType.NONSTOP_BASE_AND_UPSELL:
                analytics = listingsStore.listingResult.flightsListingsAnalytics?.moreListingsErrorAnalytics;
                break;
            }
            trackAnalytics(analytics, analyticsStore);
          }
        });
      })
      .catch(error => {
        listingsStore.setHasNonStopListingError(true);
        this.logger.logEvent(new SystemEvent(SystemEventLevel.ERROR, 'FSR_NON_STOP_SUB_PAGES_HYDRATION'), error);
      })
      .finally(() => {
        detailsAndFaresStore.setIsUpsellLoaded(true);
        listingsStore.setHasNonStopSlicesResolved(true);
        if (listingsStore.hasNonStopListingError) {
          listingsStore.updateMultiStopResultsToMainListings();
          this.resolveFinalSlices(
            listingsStore,
            errorStore,
            pageTitleStore,
            searchCriteriaStore,
            sortFilterStore,
            stepIndicatorStore
          );
        }
      });
  }

  private async fetchOnSearchClick(options: FetchOptions) {
    const {
      searchResultsStore,
      variables,
      pageStore,
      stepIndicatorStore: { setStepIndicatorVisibility, setJourneyContinuationIdInStepIndicator }
    } = this.getFetchVariables(options.stores, false, options.query);

    const response = await searchResultsStore.fetchOnSearchClick(
      variables,
      generateRequestOptions(options.request, false, pageStore.pageId)
    );

    if (!response) {
      return;
    }

    const { stepIndicator } = response;
    setJourneyContinuationIdInStepIndicator(stepIndicator?.journeyContinuationId);
    setStepIndicatorVisibility(true);
  }

  private fetchSearchResultsClientSide(options: FetchOptions) {
    if (this.pendingSearchResultsPromises.length > 0) {
      this.pendingSearchResultsPromises.forEach(p => p.cancel());
    }
    const clientPromise = this.createFlowForSearchResultsFetch(options);

    this.pendingSearchResultsPromises.push(clientPromise);

    return clientPromise;
  }

  private fetchSearchFormClientSide(options: FetchOptions) {
    if (this.pendingSearchFormPromises.length > 0) {
      this.pendingSearchFormPromises.forEach(p => p.cancel());
    }
    const clientPromise = this.createFlowForSearchFormFetch(options);

    this.pendingSearchFormPromises.push(clientPromise);

    return clientPromise;
  }

  private fetchFlexibleSearchResultsClientSide(options: FetchOptions) {
    if (this.pendingFlexibleSearchResultsPromises.length > 0) {
      this.pendingFlexibleSearchResultsPromises.forEach(p => p.cancel());
    }

    const clientPromise = this.createFlowForFlexSearchDiscoveryFetch(options);

    this.pendingFlexibleSearchResultsPromises.push(clientPromise);

    return clientPromise;
  }

  private async fetchFlightSearchMetadata(options: FetchOptions) {
    if (this.pendingSearchMetadataPromises.length > 0) {
      this.pendingSearchMetadataPromises.forEach(p => p.cancel());
    }
    const clientPromise = this.createFlowForSearchMetadataFetch(options);
    this.pendingSearchMetadataPromises.push(clientPromise);

    return clientPromise;
  }

  private hydrateStoreFSRQuery(
    options: FetchOptions,
    response: FlightsShoppingPwaFlightSearchResultsFlightsSearch.FlightsSearch,
    hydrateListing = true
  ) {
    const {
      searchCriteriaStore,
      listingsStore,
      searchFormStore,
      searchResultsStore,
      sortFilterStore,
      opinionLabStore,
      rewardsPricingInfoStore,
      pageTitleStore,
      errorStore,
      bannersStore,
      dialogsStore,
      clientMetadataStore,
      cheapestListingInfoStore,
      detailsAndFaresStore,
      gaiaIdStore,
      pageStore,
      stepIndicatorStore,
      flexibleSearchResultsStore,
      listingsHeaderPlacardsStore
    } = this.getFetchVariables(options.stores, options.isServer, options.query);

    const { isSubsequentialSearch } = searchResultsStore;

    const sortAndFilterResult = response?.sortAndFilterResult;
    const listingResult = (response && response.listingResult) || ListingsStore.DEFAULTS;
    const banners = response && response.allBanners;
    const dialogs = response && response.dialogs;
    const noListingMessaging = response && response.noListingMessaging;
    const failedRequestMessaging = response && response.failedRequestMessaging;
    const clientMetadata = response && response.clientMetadata;
    const cheapestListingInfo = response && response.cheapestListingInfo;
    const gaiaId = response && response.gaiaId;
    const stepIndicator = response?.stepIndicator;
    const updatedListingResult = { ...listingsStore.listingResult, ...listingResult };
    const listingsHeaderPlacards = response && response.listingsHeaderPlacards;

    if (
      hydrateListing &&
      (!sortFilterStore.previousFilterSnapshot.isFilterAction ||
        (sortFilterStore.previousFilterSnapshot.isFilterAction && listingResult))
    ) {
      listingsStore.listingResult.listings = [];
    }

    hydrateListing && listingsStore.hydrate(listingResult);
    sortFilterStore.hydrate({ sortAndFilterResult });
    sortFilterStore.clearPreviousFilterState();

    detailsAndFaresStore.cleanSelectedState();
    hydrateListing && listingsStore.hydrate({ listingResult: updatedListingResult });
    sortFilterStore.hydrate({ sortAndFilterResult });
    rewardsPricingInfoStore.hydrate({ rewardsPricingInfo: response && response.rewardsPricingInfo });
    pageTitleStore.hydrate({ pageTitle: response && response.pageTitle });
    opinionLabStore.hydrate(response && response.flightsOpinionLab);
    bannersStore.hydrate({ banners });
    dialogsStore.hydrate({ dialogs });
    dialogsStore.setDialogsStoreInitialState();
    errorStore.hydrate({ noListingMessaging, failedRequestMessaging });
    clientMetadataStore.hydrate({ clientMetadata });
    cheapestListingInfoStore.hydrate({ cheapestListingInfo });
    gaiaIdStore.hydrate({ gaiaId });
    searchCriteriaStore.syncWithSearchResults(response, searchFormStore.searchForm);
    listingsHeaderPlacardsStore.hydrate({ listingsHeaderPlacards });

    if (isSubsequentialSearch) {
      stepIndicatorStore.hydrate({ stepIndicator });
    }

    if (this.prevPageId !== pageStore.pageId) {
      stepIndicatorStore.hydrate({ stepIndicator });
    }

    flexibleSearchResultsStore.showGridFlag = searchCriteriaStore.shouldShowFlexSearch();
    if ('FlightsSmartNoFlightsFoundMessaging' === noListingMessaging?.__typename) {
      flexibleSearchResultsStore.showGridFlag = false;
    }
  }

  @action
  private createFlowForSearchResultsFetch = flow(function* (options: FetchOptions) {
    const {
      searchCriteriaStore,
      listingsStore,
      searchResultsStore,
      sortFilterStore,
      pageTitleStore,
      errorStore,
      bannersStore,
      dialogsStore,
      analyticsStore,
      variables,
      pageStore,
      stepIndicatorStore
    } = this.getFetchVariables(options.stores, options.isServer, options.query);
    const requestOptions = generateRequestOptions(options.request, options.isServer, pageStore.pageId);
    analyticsWrapper.resetSearchResultsAnalyticsPayLoad(analyticsStore);

    if (!sortFilterStore.previousFilterSnapshot.isFilterAction) {
      listingsStore.listingResult.listings = [];
    }
    dialogsStore.dialogs = [];
    sortFilterStore.addSkeletonsOnLoading();
    sortFilterStore.loading = true;
    listingsStore.setLoading(true);
    bannersStore.refreshBanner = !bannersStore.refreshBanner;
    pageTitleStore.setLoading(!(variables.searchFilterValuesList && variables.searchFilterValuesList.length > 0));
    errorStore.reset();
    this.setSmartShoppingCriteria(options);

    const shouldBlockNonStopCallValue = this.shouldBlockNonStopCall(
      options.query,
      listingsStore.hasNonStopConfig,
      variables?.searchPreferences?.advancedFilters?.length,
      !!variables?.sortOption,
      variables?.searchPreferences?.cabinClass,
      variables?.searchPreferences?.airline
    );
    try {
      const response: FlightsShoppingPwaFlightSearchResultsFlightsSearch.FlightsSearch = yield searchResultsStore.fetchResults(
        variables,
        requestOptions,
        analyticsStore,
        true,
        shouldBlockNonStopCallValue
      );

      const { unknownErrorIdentified } = searchResultsStore;
      errorStore.redirectToStorefront = unknownErrorIdentified;
      const { setStepIndicatorVisibility } = stepIndicatorStore;
      const typedFlightsLoadedListingResult = listingsStore.hasNonStopConfig
        ? (response?.listingResult as FlightsLoadedListingResult)
        : null;
      const hasNonStopConfigWithFiltersValue = this.shouldBlockNonStopCall(
        options.query,
        listingsStore.hasNonStopConfig,
        variables?.searchPreferences?.advancedFilters?.length,
        !!variables?.sortOption,
        variables?.searchPreferences?.cabinClass,
        variables?.searchPreferences?.airline
      );
      const shouldBlockNonStopCall =
        listingsStore.hasNonStopConfig &&
        (hasNonStopConfigWithFiltersValue ||
          !response ||
          !Object.keys(response).length ||
          typedFlightsLoadedListingResult?.listings?.length === 0);

      if (shouldBlockNonStopCall) {
        listingsStore.hydrate(
          Object.assign(ListingsStore.DEFAULTS, {
            hasNonStopConfig: true,
            hasNonStopConfigWithFilters: true,
            hasNonStopListingError: true,
            shouldShowSkeletons: listingsStore.shouldShowSkeletons,
            listingResult: {
              ...listingsStore.listingResult,
              listingsSkeletonCount: listingsStore.listingResult.listingsSkeletonCount
            }
          })
        );
        listingsStore.setHasNonStopListingError(true);

        return;
      }
      if (!response || !Object.keys(response).length) {
        errorStore.setClientFetchFailure(true);
        listingsStore.setLoading(false);
        pageTitleStore.loading = false;
        this.logger.logEvent(new SystemEvent(SystemEventLevel.INFO, 'FSR_FETCH_EMPTY_RESPONSE'));

        return;
      }

      this.hydrateStoreFSRQuery(options, response);
      listingsStore.setLoading(false);

      pageTitleStore.loading = false;
      searchCriteriaStore.newSearch = false;
      sortFilterStore.showFiltersSkeleton = false;
      sortFilterStore.loading = false;
      this.pendingSearchResultsPromises = [];

      setStepIndicatorVisibility(true);

      const skipMenu = document.querySelector<HTMLElement>('.uitk-skip-link');
      skipMenu?.parentElement?.focus();

      return;
    } catch (error) {
      setTimeout(() => {
        errorStore.clientFetchFailure = true;
        listingsStore.setLoading(false);
        pageTitleStore.loading = false;
      }, this.clientErrorRenderDelay);

      this.logger.logEvent(new SystemEvent(SystemEventLevel.CRITICAL, 'FSR_CLIENT_HYDRATION'), error);
    }
  });

  @action
  private createFlowForSearchFormFetch = flow(function* (options: FetchOptions) {
    const {
      variables,
      searchFormStore,
      searchFormResponseStore,
      searchCriteriaStore,
      pageStore,
      errorStore
    } = this.getSearchFormFetchVariables(options.stores, options.isServer);
    const requestOptions = generateRequestOptions(options.request, options.isServer, pageStore.pageId);
    searchFormStore.clearErrorRef();

    try {
      const response: FlightsShoppingPwaFlightSearchFormLoaded.FlightsSearch = yield searchFormResponseStore.fetchResults(
        variables,
        requestOptions
      );

      const searchForm = response?.searchForm;

      searchFormStore.clearErrorRef();
      searchFormStore.validateAndRestrict(searchForm);
      searchFormStore.hydrate({ searchForm });
      searchCriteriaStore.syncWithSearchFormStore(searchFormStore, true, true);

      searchFormStore.loading = false;

      this.pendingSearchFormPromises = [];

      searchFormStore.setSearchFormInitialState();

      return;
    } catch (error) {
      setTimeout(() => {
        errorStore.clientFetchFailure = true;
        searchFormStore.loading = false;
      }, this.clientErrorRenderDelay);

      this.logger.logEvent(new SystemEvent(SystemEventLevel.CRITICAL, 'SEARCH_FORM_HYDRATION'), error);
    }
  });

  @action
  private createFlowForFlexSearchDiscoveryFetch = flow(function* (options: FetchOptions) {
    const {
      variables,
      analyticsStore,
      errorStore,
      flexibleSearchResultsStore,
      flexibleSearchStore,
      cheapestListingInfoStore,
      pageStore
    } = this.getFlexSearchFetchVariables(options.stores, options.isServer);
    const requestOptions = generateRequestOptions(options.request, options.isServer, pageStore.pageId);
    flexibleSearchResultsStore.resetLoadedData();
    try {
      cheapestListingInfoStore.clear();
      let response: FlightsFlexibleDiscoverySearch.FlexibleSearch = null;
      if (
        pageStore.pageId === PageId.FLIGHTS_SEARCH_ONE_WAY ||
        pageStore.pageId === PageId.FLIGHTS_SEARCH_ROUND_TRIP_OUT_BOUND
      ) {
        response = yield flexibleSearchStore.fetchResults(variables, requestOptions, analyticsStore);
      }

      const flexibleSearch = response;
      if (flexibleSearch) {
        flexibleSearchResultsStore.hydrate({ flexibleSearchResponse: flexibleSearch });
      } else {
        flexibleSearchResultsStore.clear();
      }

      flexibleSearchResultsStore.loading = false;
      this.pendingFlexibleSearchResultsPromises = [];

      return;
    } catch (error) {
      setTimeout(() => {
        errorStore.clientFetchFailure = true;
        flexibleSearchResultsStore.loading = false;
      }, this.clientErrorRenderDelay);

      this.logger.logEvent(new SystemEvent(SystemEventLevel.ERROR, 'FLEX_DISCOVERY_SEARCH_HYDRATION'), error);
    }
  });

  @action
  private createFlowForSearchMetadataFetch = flow(function* (options: FetchOptions) {
    const { variables, flightsSearchMetadataStore, pageStore, errorStore } = this.getFetchVariables(
      options.stores,
      options.isServer,
      options.query
    );

    const requestOptions = generateRequestOptions(options.request, options.isServer, pageStore.pageId);
    try {
      const response: FlightsSearchMetadata.FlightsSearchMetadata = yield flightsSearchMetadataStore.fetchResults(
        variables,
        requestOptions
      );

      if (!response || !Object.keys(response).length) {
        errorStore.setClientFetchFailure(true);
        this.logger.logEvent(new SystemEvent(SystemEventLevel.INFO, 'FSR_METADATA_FETCH_EMPTY_RESPONSE'));

        return;
      }

      if (response) {
        flightsSearchMetadataStore.setMessages(response?.messaging);
        flightsSearchMetadataStore.hydrate({ response });
      }

      this.pendingSearchMetadataPromises = [];
    } catch (error) {
      setTimeout(() => {
        errorStore.clientFetchFailure = true;
      }, this.clientErrorRenderDelay);

      this.logger.logEvent(new SystemEvent(SystemEventLevel.ERROR, 'FSR_MEATADATA_CLIENT_HYDRATION'), error);
    }
  });

  public fetchPageData(pageData: FetchPageOptions): Promise<PageData> {
    const localizer = new Localization(pageData.context.locale);
    const query = pageData.query;
    const title = localizer.formatText('fsr.server.page.title');
    const pageId = this.getPageIdFromQuery(query);
    const pageName = getPageName(pageId);
    const navigationId = this.getNavigationIdFromQuery(query);

    return Promise.resolve({ title, pageId: pageId, pageName, navigationId });
  }

  private getListingType(
    listingsStore: ListingsStore,
    searchCriteriaStore: SearchCriteriaStore,
    query: KeyedObject
  ): FlightsListingsViewType {
    let listingType = FlightsListingsViewType.SINGLE;

    if (listingsStore.hasNonStopConfig) {
      const hasNonStopConfigWithFiltersValue = this.shouldBlockNonStopCall(
        query,
        listingsStore.hasNonStopConfig,
        searchCriteriaStore?.searchCriteria?.searchPreferences?.advancedFilters?.length,
        !!searchCriteriaStore?.searchCriteria?.sortOption,
        searchCriteriaStore?.searchCriteria?.searchPreferences?.cabinClass,
        searchCriteriaStore?.searchCriteria?.searchPreferences?.airline
      );
      if (hasNonStopConfigWithFiltersValue) {
        listingType = FlightsListingsViewType.SINGLE;
      } else {
        listingType = FlightsListingsViewType.MIXED;
      }
    }

    return listingType;
  }

  private getFetchVariables(stores: StoreContainer, isServer: boolean, query: KeyedObject) {
    const searchCriteriaStore = stores.get<SearchCriteriaStore>('searchCriteriaStore');
    const searchFormStore = stores.get<SearchFormStore>('searchFormStore');
    const listingsStore = stores.get<ListingsStore>('listingsStore');
    const searchResultsStore = stores.get<SearchResultsStore>('searchResults');
    const sortFilterStore = stores.get<SortFilterStore>('sortFilterStore');
    const rewardsPricingInfoStore = stores.get<RewardsPricingInfoStore>('rewardsPricingInfoStore');
    const pageTitleStore = stores.get<PageTitleStore>('pageTitleStore');
    const opinionLabStore = stores.get<OpinionLabStore>('opinionLabStore');
    const errorStore = stores.get<ErrorStore>('errorStore');
    const bannersStore = stores.get<BannersStore>('bannersStore');
    const dialogsStore = stores.get<DialogsStore>('dialogsStore');
    const bernieContext: ContextStore = stores.get<ContextStore>('context');
    const analyticsStore = stores.get<AnalyticsStore>('analytics');
    const cheapestListingInfoStore = stores.get<CheapestListingInfoStore>('cheapestListingInfoStore');
    const flexibleSearchResultsStore = stores.get<FlexibleSearchResultsStore>('flexibleSearchResultsStore');
    const context = getBexApiContext(bernieContext);
    const loadSwitch = isServer ? FlightsQueryState.LOADING : FlightsQueryState.LOADED;
    const clientMetadataStore = stores.get<ClientMetadataStore>('clientMetadataStore');
    const gaiaIdStore = stores.get<GaiaIdStore>('gaiaIdStore');
    const stepIndicatorStore = stores.get<StepIndicatorStore>('stepIndicatorStore');
    const pageStore = stores.get<PageStore>('page');
    const experimentStore = stores.get<ExperimentStore>('experiment');
    const detailsAndFaresStore = stores.get<DetailsAndFaresStore>('detailsAndFaresStore');
    const advertisingStore = stores.get<AdvertisingStore>('advertising');
    const listingsHeaderPlacardsStore = stores.get<ListingsHeaderPlacardsStore>('listingsHeaderPlacardsStore');
    const creditRedemptionStore = stores.get<CreditRedemptionStore>('creditRedemptionStore');
    const virtualAgentStore = stores.get<VirtualAgentStore>('virtualAgentStore');
    const flightsSearchMetadataStore = stores.get<FlightsSearchMetadataStore>('flightsSearchMetadataStore');
    const listingType = this.getListingType(listingsStore, searchCriteriaStore, query);

    searchCriteriaStore.searchCriteria.queryState = loadSwitch;

    const variables = {
      context,
      rewardsOption: searchCriteriaStore.rewardsOption,
      flightsListingsViewType: listingType,
      ...searchCriteriaStore.searchCriteria
    } as FlightsShoppingPwaFlightSearchResultsFlightsSearch.Variables;

    return {
      analyticsStore,
      searchCriteriaStore,
      searchFormStore,
      listingsStore,
      searchResultsStore,
      sortFilterStore,
      rewardsPricingInfoStore,
      pageTitleStore,
      opinionLabStore,
      errorStore,
      bannersStore,
      dialogsStore,
      variables,
      clientMetadataStore,
      cheapestListingInfoStore,
      gaiaIdStore,
      stepIndicatorStore,
      pageStore,
      flexibleSearchResultsStore,
      experimentStore,
      detailsAndFaresStore,
      advertisingStore,
      listingsHeaderPlacardsStore,
      creditRedemptionStore,
      virtualAgentStore,
      flightsSearchMetadataStore
    };
  }

  private getSearchFormFetchVariables(stores: StoreContainer, isServer: boolean) {
    const searchCriteriaStore = stores.get<SearchCriteriaStore>('searchCriteriaStore');
    const searchFormStore = stores.get<SearchFormStore>('searchFormStore');
    const searchFormResponseStore = stores.get<SearchFormResponseStore>('searchFormResponseStore');
    const pageStore = stores.get<PageStore>('page');
    const errorStore = stores.get<ErrorStore>('errorStore');

    const bernieContext: ContextStore = stores.get<ContextStore>('context');
    const context = getBexApiContext(bernieContext);
    const loadSwitch = isServer ? FlightsQueryState.LOADING : FlightsQueryState.LOADED;
    searchCriteriaStore.searchFormCriteria.queryState = loadSwitch;
    const variables = { context, ...searchCriteriaStore.searchFormCriteria };

    return {
      variables,
      searchFormStore,
      searchFormResponseStore,
      searchCriteriaStore,
      pageStore,
      errorStore
    };
  }

  private getFlexSearchFetchVariables(stores: StoreContainer, isServer: boolean) {
    const searchCriteriaStore = stores.get<SearchCriteriaStore>('searchCriteriaStore');
    const errorStore = stores.get<ErrorStore>('errorStore');
    const flexibleSearchResultsStore = stores.get<FlexibleSearchResultsStore>('flexibleSearchResultsStore');
    const flexibleSearchStore = stores.get<FlexibleSearchStore>('flexibleSearchStore');
    const cheapestListingInfoStore = stores.get<CheapestListingInfoStore>('cheapestListingInfoStore');
    const analyticsStore = stores.get<AnalyticsStore>('analytics');
    const bernieContext: ContextStore = stores.get<ContextStore>('context');
    const context = getBexApiContext(bernieContext);
    const loadSwitch = isServer ? FlightsQueryState.LOADING : FlightsQueryState.LOADED;
    searchCriteriaStore.flexSearchCriteria.queryState = loadSwitch;
    const pageStore = stores.get<PageStore>('page');
    const variables = { context, ...searchCriteriaStore.flexSearchCriteria };

    return {
      variables,
      analyticsStore,
      errorStore,
      flexibleSearchResultsStore,
      flexibleSearchStore,
      cheapestListingInfoStore,
      pageStore
    };
  }
}
